export interface GardenStageDefinition {
  id: number;
  name: string;
  threshold: number;
  arrivalReward: string;
  description: string;
  accent: string;
}

export interface GardenerRankDefinition {
  tier: string;
  title: string;
  threshold: number;
  reward: string;
  color: string;
}

/**
 * Product-facing progression copy. Thresholds and names mirror server/src/garden.ts.
 * Descriptions only claim renderer features that exist at the corresponding stage.
 */
export const GARDEN_STAGES: readonly GardenStageDefinition[] = [
  { id: 0, name: "Bare Soil", threshold: 0, arrivalReward: "The shared plot opens", description: "A clean plot waiting for the community's first activity.", accent: "#b98354" },
  { id: 1, name: "Grass & Flowers", threshold: 200, arrivalReward: "Grass and first wildflowers", description: "The field softens and messages begin leaving visible blooms.", accent: "#73b85d" },
  { id: 2, name: "Paths & Shrubs", threshold: 500, arrivalReward: "Paths and border shrubs", description: "Routes appear and the garden starts to feel intentionally shaped.", accent: "#d6a25c" },
  { id: 3, name: "Pond", threshold: 1_000, arrivalReward: "A pond at the garden's heart", description: "Water becomes the first major shared landmark.", accent: "#54b9d0" },
  { id: 4, name: "Gazebo & Trees", threshold: 2_000, arrivalReward: "Gazebo, bridges, trees, and seasons", description: "A gathering place and mature canopy transform the map silhouette.", accent: "#e19d69" },
  { id: 5, name: "Festival Grounds", threshold: 4_000, arrivalReward: "Festival lights and celebration details", description: "The garden shifts into a communal celebration space.", accent: "#eb6f92" },
  { id: 6, name: "The Village", threshold: 7_000, arrivalReward: "Village square and working structures", description: "Cottages, a market, greenhouse, windmill, and civic landmarks arrive.", accent: "#e7b85f" },
  { id: 7, name: "Crystal Gardens", threshold: 11_000, arrivalReward: "Crystals, mushroom rings, and enchanted flowers", description: "Rare flora and luminous details make the world feel less ordinary.", accent: "#9b81e3" },
  { id: 8, name: "Starlight Grove", threshold: 16_000, arrivalReward: "Observatory and wishing well", description: "Night-sky landmarks give the garden a more celestial identity.", accent: "#6daee8" },
  { id: 9, name: "Emperor's Orchard", threshold: 22_000, arrivalReward: "Orchards, fruit trees, topiary, and a grand arch", description: "Cultivated groves create a distinct high-stage district.", accent: "#ef8c6d" },
  { id: 10, name: "Aurora Fields", threshold: 29_000, arrivalReward: "Auroras, floating islands, and glowing mushrooms", description: "The sky and terrain become overtly magical.", accent: "#64d5bc" },
  { id: 11, name: "Celestial Haven", threshold: 37_000, arrivalReward: "Four celestial sanctuary pylons", description: "Crystal pylons establish a new sanctuary around the central crossroads.", accent: "#7ea7f2" },
  { id: 12, name: "Elysian Meadows", threshold: 46_000, arrivalReward: "Flowering Elysian meadow seals", description: "Two living meadow rings appear between the sanctuary pylons.", accent: "#d58bea" },
  { id: 13, name: "Paradise Bloom", threshold: 56_000, arrivalReward: "Giant paradise blooms", description: "Large ceremonial flowers flank the main northern path.", accent: "#ef7ea9" },
  { id: 14, name: "Infinity Garden", threshold: 67_000, arrivalReward: "The suspended infinity crest", description: "A luminous double loop marks the final celestial tier.", accent: "#8d87f2" },
  { id: 15, name: "Garden of Legends", threshold: 79_000, arrivalReward: "The Legend arch and permanent celebration", description: "A gold-capped arch completes the world and frames its final district.", accent: "#f0c75e" },
] as const;

export const GARDENER_RANKS: readonly GardenerRankDefinition[] = [
  { tier: "VISITOR", title: "Visitor", threshold: 0, reward: "Core gardener styles and colors", color: "#78909C" },
  { tier: "SPROUT", title: "Sprout", threshold: 1, reward: "Tan skin, blonde hair, and yellow, purple, brown, and olive clothing", color: "#546E7A" },
  { tier: "SEEDLING", title: "Seedling", threshold: 50, reward: "More skin tones plus auburn, red, orange, pink, tan, and maroon options", color: "#66BB6A" },
  { tier: "PLANTER", title: "Planter", threshold: 150, reward: "Olive and deep skin, platinum and grey hair, teal tops, and denim", color: "#EF6C00" },
  { tier: "CULTIVATOR", title: "Cultivator", threshold: 300, reward: "Wall posting plus bold hair colors and monochrome clothing", color: "#1565C0" },
  { tier: "GROUNDSKEEPER", title: "Groundskeeper", threshold: 600, reward: "Golden skin, silver hair, and emerald and forest clothing", color: "#2E7D32" },
  { tier: "MASTER_GARDENER", title: "Master Gardener", threshold: 1_000, reward: "Rose skin, purple hair, jade tops, and slate bottoms", color: "#00897B" },
  { tier: "WARDEN", title: "Warden", threshold: 2_000, reward: "White hair and a royal clothing palette", color: "#6A1B9A" },
  { tier: "LEGEND", title: "Legend", threshold: 5_000, reward: "Gold palette and the Legend crown", color: "#D4AF37" },
] as const;

export const GARDENER_RANK_ORDER: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(GARDENER_RANKS.map((rank, index) => [rank.tier, index])),
);

export function getGardenStage(stage: number): GardenStageDefinition {
  const index = Math.max(0, Math.min(GARDEN_STAGES.length - 1, Math.floor(stage)));
  return GARDEN_STAGES[index]!;
}

export function getNextGardenStage(stage: number): GardenStageDefinition | null {
  return GARDEN_STAGES[Math.floor(stage) + 1] ?? null;
}

export function getGardenerRank(tier?: string | null): GardenerRankDefinition {
  return GARDENER_RANKS.find((rank) => rank.tier === tier) ?? GARDENER_RANKS[0]!;
}

export function getGardenerRankByPoints(points: number): GardenerRankDefinition {
  const safePoints = Number.isFinite(points) ? Math.max(0, points) : 0;
  for (let index = GARDENER_RANKS.length - 1; index >= 0; index -= 1) {
    const rank = GARDENER_RANKS[index]!;
    if (safePoints >= rank.threshold) return rank;
  }
  return GARDENER_RANKS[0]!;
}

export function getNextGardenerRank(tier?: string | null): GardenerRankDefinition | null {
  const index = GARDENER_RANKS.findIndex((rank) => rank.tier === tier);
  return GARDENER_RANKS[index < 0 ? 1 : index + 1] ?? null;
}

export function calculateStageProgress(totalPoints: number, stage: number): number {
  const current = getGardenStage(stage);
  const next = getNextGardenStage(stage);
  if (!next) return 1;
  const band = next.threshold - current.threshold;
  return band > 0 ? Math.max(0, Math.min(1, (totalPoints - current.threshold) / band)) : 1;
}

export function calculateGardenerRankProgress(points: number): number {
  const safePoints = Number.isFinite(points) ? Math.max(0, points) : 0;
  const current = getGardenerRankByPoints(safePoints);
  const next = getNextGardenerRank(current.tier);
  if (!next) return 1;
  const band = next.threshold - current.threshold;
  return band > 0 ? Math.max(0, Math.min(1, (safePoints - current.threshold) / band)) : 1;
}
